function getScreenResolution() {
    return { width: window.screen.width, height: window.screen.height };
}
function getTimeZone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
    catch {
        return 'UTC';
    }
}
export function getBrowserLocale() {
    try {
        return navigator.language || 'en-US';
    }
    catch {
        return 'en-US';
    }
}
// ---------------------------------------------------------------------------
// Lightweight UA parsing — no third-party library needed
// ---------------------------------------------------------------------------
const UA = typeof navigator !== 'undefined' ? navigator.userAgent : '';
/** Extract first match's groups from a UA string */
function match(ua, ...patterns) {
    for (const p of patterns) {
        const m = ua.match(p);
        if (m)
            return m;
    }
    return null;
}
function parseBrowser(ua) {
    // Order matters — more specific first
    const tests = [
        [/EdgA?\/([\d.]+)/i, 'Edge'],
        [/OPR\/([\d.]+)/i, 'Opera'],
        [/Brave\/([\d.]+)/i, 'Brave'],
        [/Vivaldi\/([\d.]+)/i, 'Vivaldi'],
        [/SamsungBrowser\/([\d.]+)/i, 'Samsung Internet'],
        [/UCBrowser\/([\d.]+)/i, 'UC Browser'],
        [/Firefox\/([\d.]+)/i, 'Firefox'],
        [/CriOS\/([\d.]+)/i, 'Chrome'], // Chrome on iOS
        [/FxiOS\/([\d.]+)/i, 'Firefox'], // Firefox on iOS
        [/Chrome\/([\d.]+)/i, 'Chrome'],
        [/Version\/([\d.]+).*Safari/i, 'Safari'],
        [/Safari\/([\d.]+)/i, 'Safari'],
    ];
    for (const [re, name] of tests) {
        const m = ua.match(re);
        if (m) {
            const version = m[1] || '';
            return { name, version, major: version.split('.')[0] || '' };
        }
    }
    return { name: '', version: '', major: '' };
}
function parseOS(ua) {
    const m = match(ua, /Windows NT ([\d.]+)/i) ||
        match(ua, /Mac OS X ([\d_.]+)/i) ||
        match(ua, /Android ([\d.]+)/i) ||
        match(ua, /iPhone OS ([\d_]+)/i) ||
        match(ua, /iPad.*OS ([\d_]+)/i) ||
        match(ua, /CrOS[\w ]*\/([\d.]+)/i) ||
        match(ua, /Linux/i);
    if (!m)
        return { name: '', version: '' };
    const raw = m[0];
    const version = (m[1] || '').replace(/_/g, '.');
    if (/Windows/i.test(raw)) {
        const ntMap = {
            '10.0': '10', '6.3': '8.1', '6.2': '8', '6.1': '7', '6.0': 'Vista', '5.1': 'XP',
        };
        return { name: 'Windows', version: ntMap[version] || version };
    }
    if (/Mac OS X/i.test(raw))
        return { name: 'macOS', version };
    if (/Android/i.test(raw))
        return { name: 'Android', version };
    if (/iPhone|iPad/i.test(raw))
        return { name: 'iOS', version };
    if (/CrOS/i.test(raw))
        return { name: 'Chrome OS', version };
    if (/Linux/i.test(raw))
        return { name: 'Linux', version: '' };
    return { name: '', version: '' };
}
function parseDevice(ua) {
    if (/iPad/i.test(ua))
        return { type: 'tablet', vendor: 'Apple', model: 'iPad' };
    if (/iPhone/i.test(ua))
        return { type: 'mobile', vendor: 'Apple', model: 'iPhone' };
    if (/iPod/i.test(ua))
        return { type: 'mobile', vendor: 'Apple', model: 'iPod' };
    if (/Android/i.test(ua)) {
        const m = ua.match(/Android[\s\d.]+;\s*([^)]+?)(?:\s+Build)/i);
        const mobile = /Mobile/i.test(ua);
        return { type: mobile ? 'mobile' : 'tablet', model: m?.[1]?.trim() };
    }
    return undefined;
}
function parseEngine(ua) {
    const m = match(ua, /AppleWebKit\/([\d.]+)/i) ||
        match(ua, /Gecko\/([\d.]+)/i) ||
        match(ua, /Trident\/([\d.]+)/i) ||
        match(ua, /Presto\/([\d.]+)/i);
    if (!m)
        return { name: '', version: '' };
    const raw = m[0];
    const version = m[1] || '';
    if (/AppleWebKit/i.test(raw))
        return { name: 'WebKit', version };
    if (/Gecko/i.test(raw))
        return { name: 'Gecko', version };
    if (/Trident/i.test(raw))
        return { name: 'Trident', version };
    if (/Presto/i.test(raw))
        return { name: 'Presto', version };
    return { name: '', version: '' };
}
function parseCPU(ua) {
    if (/x86_64|x64|amd64|Win64/i.test(ua))
        return { architecture: 'amd64' };
    if (/ia32|x86/i.test(ua))
        return { architecture: 'ia32' };
    if (/aarch64|arm64/i.test(ua))
        return { architecture: 'arm64' };
    if (/arm/i.test(ua))
        return { architecture: 'arm' };
    return { architecture: '' };
}
// ---------------------------------------------------------------------------
// Flags
// ---------------------------------------------------------------------------
function detectIsBot() {
    if (navigator.webdriver)
        return true;
    return /bot|crawl|spider|slurp|Googlebot|bingbot|yandex|baidu|duckduck|facebookexternalhit|Twitterbot|linkedinbot|embedly|quora|pinterest|redditbot|Slackbot|Discordbot|WhatsApp|TelegramBot|Applebot/i.test(UA);
}
function detectIsChromeFamily(browserName) {
    return /Chrome|Chromium|Edge|Brave|Opera|Vivaldi|Arc/i.test(browserName);
}
function detectIsStandalonePWA() {
    return window.matchMedia('(display-mode: standalone)').matches
        || navigator.standalone === true;
}
function detectIsAppleSilicon(osName) {
    if (!/mac/i.test(osName))
        return false;
    return navigator.maxTouchPoints > 0;
}
// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------
export function collectDeviceInfo() {
    if (typeof window === 'undefined') {
        return {
            userAgent: {
                name: '',
                browser: { major: '', name: '', version: '' },
                os: { name: '', version: '' },
                device: undefined,
                engine: { name: '', version: '' },
                cpu: { architecture: '' },
            },
            screenResolution: { width: 0, height: 0 },
            timeZone: 'UTC',
            flags: { isBot: false, isChromeFamily: false, isStandalonePWA: false, isAppleSilicon: false },
        };
    }
    const browser = parseBrowser(UA);
    const os = parseOS(UA);
    const device = parseDevice(UA);
    const engine = parseEngine(UA);
    const cpu = parseCPU(UA);
    let flags;
    try {
        flags = {
            isBot: detectIsBot(),
            isChromeFamily: detectIsChromeFamily(browser.name),
            isStandalonePWA: detectIsStandalonePWA(),
            isAppleSilicon: detectIsAppleSilicon(os.name),
        };
    }
    catch {
        flags = { isBot: false, isChromeFamily: false, isStandalonePWA: false, isAppleSilicon: false };
    }
    return {
        userAgent: { name: UA, browser, os, device, engine, cpu },
        screenResolution: getScreenResolution(),
        timeZone: getTimeZone(),
        flags,
    };
}
export function getUrlParams() {
    if (typeof window === 'undefined')
        return {};
    const params = new URLSearchParams(window.location.search);
    return {
        locale: params.get('locale') || undefined,
        currency: params.get('currency') || undefined,
        utmSource: params.get('utm_source') || undefined,
        utmMedium: params.get('utm_medium') || undefined,
        utmCampaign: params.get('utm_campaign') || undefined,
        gclid: params.get('gclid') || undefined,
    };
}
